import { useMemo, useState, useEffect } from 'react';
import { AlertTriangle, Plus, Trash2 } from 'lucide-react';
import { idleDriverSummary, idleHostSummary, idleMoney, idleText } from '@evi/shared';
import { renderInline } from '@evi/ui';
import {
  IDLE_FEE_MAX_MINUTES, IDLE_GRACE_DEFAULT_MINUTES, IDLE_GRACE_MAX_MINUTES, IDLE_GRACE_MESSAGE,
  IDLE_GRACE_MIN_MINUTES, IDLE_RATE_MAX_CENTS_PER_MIN, IDLE_RATE_MAX_MESSAGE, isValidIdleGraceMinutes,
} from '@evi/shared';
import { createTariff, updateTariff, getDisplayErrorMessage, type Tariff } from '@/lib/api';
import { TimeWindowRows } from './TimeWindowRows';
import styles from './Form.module.css';
import { Switch } from '@/components/ui/Switch';

interface TariffFormProps {
  existing?: Tariff;
  onSuccess: () => void;
  onCancel: () => void;
}

const TYPE_OPTIONS: Array<{ value: Tariff['type']; label: string; help: string }> = [
  { value: 'flat', label: 'Flat', help: 'Single per-kWh rate' },
  { value: 'time_of_use', label: 'Time of Use', help: 'Different rates by hour and day' },
  { value: 'tiered', label: 'Tiered', help: 'Rate changes at usage thresholds' },
];

interface TouEntry {
  day_of_week: number[];
  start_hour: number;
  end_hour: number;
  energy_rate: number;
}

interface TierEntry {
  /** Upper bound of the tier — kWh for per_kwh tariffs, hours
   *  for per_hour. Null = "and above". Renamed from up_to_kwh in
   *  Commit D for unit-neutrality; the old field is read on load
   *  via the loader below and never written by this form. */
  tier_max: number | null;
  energy_rate: number;
}

export function TariffForm({ existing, onSuccess, onCancel }: TariffFormProps) {
  const locale = typeof navigator === 'undefined' ? 'en' : navigator.language;
  // A new tariff picks USD or CAD (a Canadian location takes only CAD
  // tariffs); an existing one keeps its currency for good (the Api answers
  // 409 TARIFF_CURRENCY_IMMUTABLE to a change).
  const [newCurrency, setNewCurrency] = useState<'USD' | 'CAD'>('USD');
  const currency = existing?.currency ?? newCurrency;
  // The idle fee is optional (Warren, 2026-10-25: "a tariff with no idle fee
  // saves"). Off, the idle inputs are disabled and nothing idle is sent, so
  // no idle field can block the save and billing pins no idle fee.
  const [idleOn, setIdleOn] = useState((existing?.rates.time_rate ?? 0) > 0);
  const [name, setName] = useState(existing?.name ?? '');
  const [type, setType] = useState<Tariff['type']>(existing?.type ?? 'flat');
  // Commit D — per-tariff unit selector. Defaults to per_kwh for
  // backward-compat with every existing tariff. Per-hour switches
  // the whole tariff (flat, all TOU windows, all tiers) to
  // duration-billed pricing.
  const [rateType, setRateType] = useState<'per_kwh' | 'per_hour'>(
    existing?.rate_type ?? 'per_kwh',
  );
  const [energyRate, setEnergyRate] = useState(String(existing?.rates.energy_rate ?? '30'));
  const [timeRate, setTimeRate] = useState(existing?.rates.time_rate != null ? String(existing.rates.time_rate / 100) : '');
  const [idleLimit, setIdleLimit] = useState(existing?.rates.idle_max_charge_cents != null ? String(existing.rates.idle_max_charge_cents / 100) : '');
  // The most a session pays in idle: the ¢/min rate over the billable cap.
  const timeRateCents = Math.round(parseFloat(timeRate) * 100);
  const idleMaxCents = idleOn && Number.isFinite(timeRateCents) && timeRateCents > 0
    ? Math.min(Math.round(timeRateCents * IDLE_FEE_MAX_MINUTES), idleLimit.trim() === '' ? Infinity : Math.round(Number(idleLimit) * 100))
    : null;
  // The idle fee's grace (Warren, 2026-09-28: required with every idle fee,
  // 5 to 30 minutes, default 15). A tariff saved before the field shows
  // the default, which is what billing pins for it.
  const [idleGrace, setIdleGrace] = useState(
    String(existing?.rates.idle_grace_minutes ?? IDLE_GRACE_DEFAULT_MINUTES),
  );
  const hasIdleFee = idleOn && Number.isFinite(timeRateCents) && timeRateCents > 0;
  // The session start fee (rates.flat_fee) is retired (Tier 2): the
  // operator no longer sets any fee, and the API no longer accepts it.
  // EVI's connection fee is per charger (Charger.connection_fee_cents,
  // super_admin, ChargerDetailPage). A tariff that still carries one is
  // shown read-only; it bills only on sessions that started before the
  // fee was pinned. Saving REPLACES `rates` (the PATCH $sets the nested
  // object whole), so a save drops the legacy value.
  const legacyFlatFeeCents = existing?.rates.flat_fee ?? 0;
  const [taxRate, setTaxRate] = useState(String(existing?.tax_rate ?? '0.0875'));
  const [isDefault, setIsDefault] = useState(existing?.is_default ?? false);
  const [touEntries, setTouEntries] = useState<TouEntry[]>(
    existing?.rates.time_of_use_schedule ?? [
      { day_of_week: [1, 2, 3, 4, 5], start_hour: 0, end_hour: 6, energy_rate: 12 },
      { day_of_week: [1, 2, 3, 4, 5], start_hour: 6, end_hour: 22, energy_rate: 30 },
      { day_of_week: [0, 6], start_hour: 0, end_hour: 24, energy_rate: 25 },
    ]
  );
  const [tierEntries, setTierEntries] = useState<TierEntry[]>(() => {
    if (!existing?.rates.tiered_schedule) {
      return [
        { tier_max: 10, energy_rate: 30 },
        { tier_max: 50, energy_rate: 25 },
        { tier_max: null, energy_rate: 20 },
      ];
    }
    // Loader normalizes pre-Commit-D docs: prefer tier_max if set,
    // fall back to up_to_kwh. New writes always emit tier_max only;
    // the API contract retains up_to_kwh for one release for safety
    // (a follow-up commit drops it after the migration script runs
    // and the legacy field is verified absent in prod data).
    return existing.rates.tiered_schedule.map((t) => ({
      tier_max: t.tier_max ?? t.up_to_kwh ?? null,
      energy_rate: t.energy_rate,
    }));
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Variant-audit (docs/billing/variant-audit.md) LOW finding:
  // per-hour tariffs at typical rates ($1.50-$3/hour) on short
  // sessions chronically under-cover the Stripe minimum (30¢ +
  // 2.9%) + per-kWh building reimbursement, producing $0 operator
  // payout. The threshold is rate × typical_duration ≥ Stripe
  // minimum + reasonable building cost. Founder-tuned at 200¢/hour
  // ($2/hour) — below that, a 30-min session at typical CA
  // residential per-kWh building cost wipes operator margin.
  //
  // Checks every rate field that's currently in the active type's
  // schedule. Subthreshold = warning visible. Doesn't block submit
  // — guidance, not validation.
  const PER_HOUR_LOW_THRESHOLD_CENTS = 200;
  const isPerHourUndercover = useMemo<boolean>(() => {
    if (rateType !== 'per_hour') return false;
    const ratesToCheck: number[] = [];
    // Base energy_rate always applies (acts as fallback for TOU/tiered too).
    const base = parseFloat(energyRate);
    if (Number.isFinite(base) && base > 0) ratesToCheck.push(base);
    if (type === 'time_of_use') {
      for (const w of touEntries) {
        if (Number.isFinite(w.energy_rate) && w.energy_rate > 0) {
          ratesToCheck.push(w.energy_rate);
        }
      }
    }
    if (type === 'tiered') {
      for (const t of tierEntries) {
        if (Number.isFinite(t.energy_rate) && t.energy_rate > 0) {
          ratesToCheck.push(t.energy_rate);
        }
      }
    }
    // Any non-zero rate below threshold triggers the warning.
    return ratesToCheck.some((r) => r < PER_HOUR_LOW_THRESHOLD_CENTS);
  }, [rateType, type, energyRate, touEntries, tierEntries]);

  useEffect(() => {
    if (existing) {
      setName(existing.name);
      setType(existing.type);
      setRateType(existing.rate_type ?? 'per_kwh');
      setEnergyRate(String(existing.rates.energy_rate));
    }
  }, [existing]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const grace = Number(idleGrace);
    if (idleOn && !hasIdleFee) {
      setError('Enter the idle fee per minute, or untick "Charge an idle fee".');
      return;
    }
    if (idleOn && idleLimit.trim() !== '' && (!Number.isFinite(Number(idleLimit)) || Number(idleLimit) < 0)) {
      setError('Maximum idle charge must be zero or more, or empty for no additional limit.'); return;
    }
    if (hasIdleFee && timeRateCents > IDLE_RATE_MAX_CENTS_PER_MIN) {
      setError(IDLE_RATE_MAX_MESSAGE);
      return;
    }
    // A per-hour tariff bills no idle and its grace input is disabled, so the
    // grace is not asked for there (the server keeps the stored one or 15).
    const graceApplies = hasIdleFee && rateType !== 'per_hour';
    if (graceApplies && (idleGrace.trim() === '' || !isValidIdleGraceMinutes(grace))) {
      setError(IDLE_GRACE_MESSAGE);
      return;
    }
    setSubmitting(true);

    try {
      const body = {
        name: name.trim(),
        type,
        rate_type: rateType,
        // A tariff's currency never changes (item 3: PATCH answers 409
        // TARIFF_CURRENCY_IMMUTABLE for another one), so an edit re-sends
        // the stored one, a no-op, and a CAD tariff stays editable here. A
        // new tariff is USD, as before.
        currency: existing?.currency ?? newCurrency,
        rates: {
          energy_rate: parseFloat(energyRate) || 0,
          // No idle fee: no rate, no grace, no maximum (the PATCH replaces
          // `rates` whole, so this also removes a fee the tariff had).
          time_rate: hasIdleFee ? timeRateCents : undefined,
          idle_grace_minutes: graceApplies ? grace : undefined,
          idle_max_charge_cents: !hasIdleFee || idleLimit.trim() === '' ? null : Math.round(Number(idleLimit) * 100),
          // No flat_fee, ever: see legacyFlatFeeCents above.
          time_of_use_schedule: type === 'time_of_use' ? touEntries : undefined,
          tiered_schedule: type === 'tiered' ? tierEntries : undefined,
        },
        tax_rate: parseFloat(taxRate) || 0,
        is_default: isDefault,
      };

      if (existing) {
        await updateTariff(existing._id, body);
      } else {
        await createTariff(body);
      }
      onSuccess();
    } catch (err) {
      // The server's own message, e.g. the Site electricity ceiling on a
      // tariff bound as a Location's electricity tariff.
      setError(await getDisplayErrorMessage(err, 'Failed to save tariff'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      {error && <div className={styles.formError}>{error}</div>}

      <div className={styles.field}>
        <label className={`${styles.label} ${styles.required}`}>Name</label>
        <input
          className={styles.input}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Standard Residential"
          required
          maxLength={100}
        />
      </div>

      <div className={styles.field}>
        <label className={`${styles.label} ${styles.required}`} htmlFor="tariff-currency">Currency</label>
        <select
          id="tariff-currency"
          className={styles.select}
          value={currency}
          disabled={!!existing}
          onChange={(e) => setNewCurrency(e.target.value === 'CAD' ? 'CAD' : 'USD')}
        >
          <option value="USD">USD (United States locations)</option>
          <option value="CAD">CAD (Canadian locations)</option>
        </select>
        <p className={styles.hint}>
          {existing
            ? "A tariff's currency never changes. For another currency, create a new tariff and assign it."
            : 'A location only uses tariffs in its own currency. This cannot be changed after the tariff is created.'}
        </p>
      </div>

      <div className={styles.row}>
        <div className={styles.field}>
          <label className={`${styles.label} ${styles.required}`}>Type</label>
          <select className={styles.select} value={type} onChange={(e) => setType(e.target.value as Tariff['type'])}>
            {TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <p className={styles.hint}>{TYPE_OPTIONS.find((o) => o.value === type)?.help}</p>
        </div>
        <div className={styles.field}>
          <label className={styles.label}>Unit</label>
          <select
            className={styles.select}
            value={rateType}
            onChange={(e) => setRateType(e.target.value === 'per_hour' ? 'per_hour' : 'per_kwh')}
          >
            <option value="per_kwh">¢/kWh</option>
            <option value="per_hour">¢/hour</option>
          </select>
          <p className={styles.hint}>
            {rateType === 'per_hour'
              ? 'Bills by session duration regardless of energy delivered. Useful for idle fees or time-bundled pricing.'
              : 'Bills by kWh delivered (default).'}
          </p>
        </div>
      </div>

      {/* Under-cover warning (variant-audit LOW finding). Renders
          when rate_type is per_hour AND at least one rate field
          is below the $2/hour threshold. Advisory only; doesn't
          block save. */}
      {isPerHourUndercover && (
        <div
          className={styles.formError}
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            background: 'color-mix(in srgb, var(--evi-warn, #f59e0b) 12%, transparent)',
            borderColor: 'color-mix(in srgb, var(--evi-warn, #f59e0b) 40%, transparent)',
            color: 'var(--evi-warn, #f59e0b)',
          }}
        >
          <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>
            Per-hour rates below ~$2/hour may produce $0 operator payout after
            Stripe minimums + building reimbursement on short sessions. Consider
            raising the rate.
          </span>
        </div>
      )}

      <div className={styles.row}>
        <div className={styles.field}>
          <label className={`${styles.label} ${styles.required}`}>Energy rate (¢/kWh)</label>
          <input
            className={styles.input}
            type="number"
            step="0.01"
            min={0}
            value={energyRate}
            onChange={(e) => setEnergyRate(e.target.value)}
            required
          />
          <p className={styles.hint}>Base rate. {type !== 'flat' ? 'Used as fallback when no schedule matches.' : ''}</p>
        </div>
        <div className={styles.field}>
          <label className={styles.label}>Tax rate (decimal)</label>
          <input
            className={styles.input}
            type="number"
            step="0.0001"
            min={0}
            max={1}
            value={taxRate}
            onChange={(e) => setTaxRate(e.target.value)}
          />
          <p className={styles.hint}>0.0875 = 8.75%</p>
        </div>
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--color-tx)' }}>
        <input
          type="checkbox"
          checked={idleOn}
          onChange={(e) => { setIdleOn(e.target.checked); setError(''); }}
          aria-describedby="tariff-idle-toggle-hint"
        />
        Charge an idle fee
      </label>
      <p id="tariff-idle-toggle-hint" className={styles.hint} style={{ marginTop: -8 }}>
        {idleOn
          ? 'Billed per minute once charging finishes and the grace period ends, until the driver unplugs.'
          : 'No idle fee. Drivers pay for energy only (and the connection fee where one is set on the charger).'}
      </p>

      <div className={styles.row}>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="tariff-idle-rate">{idleText(locale, 'rate', { rate: currency }).replace(/\*\*/g, '')}</label>
          <div className={styles.currencyInput}>
            <span className={styles.currencyPrefix} aria-hidden>{currency === 'CAD' ? 'CA$' : 'US$'}</span>
          <input id="tariff-idle-rate"
            className={styles.input}
            type="number"
            step="0.01"
            min={0}
            max={2}
            value={idleOn ? timeRate : ''}
            onChange={(e) => setTimeRate(e.target.value)}
            placeholder={idleOn ? 'per minute' : 'no idle fee'}
            disabled={!idleOn}
            required={idleOn}
          />
            <span className={styles.currencyPrefix} aria-hidden>/min</span>
          </div>
          {/* Idle bills once charging stops, for at most
              IDLE_FEE_MAX_MINUTES a session (Warren, 2026-09-27: a
              deterrent, not a revenue line). A per-hour tariff already
              bills the whole stay, so computeIdleMinutes bills it none. */}
          <p className={styles.hint} style={{ fontVariantNumeric: 'tabular-nums' }}>
            {rateType === 'per_hour'
              ? idleText(locale, 'perHour')
              : <>
                  {renderInline(idleText(locale, 'rateHint', { max: idleMoney(IDLE_RATE_MAX_CENTS_PER_MIN, currency, locale) }))}
                  {idleMaxCents != null && <><br />{renderInline(idleHostSummary(timeRateCents,
                    idleLimit.trim() === '' ? null : Math.round(Number(idleLimit) * 100), currency, locale))}<br />
                    {renderInline(idleDriverSummary({ time_rate: timeRateCents, idle_grace_minutes: Number(idleGrace),
                      idle_max_charge_cents: idleLimit.trim() === '' ? null : Math.round(Number(idleLimit) * 100) }, currency, locale))}</>}
                </>}
          </p>
        </div>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="tariff-idle-maximum">{idleText(locale, 'limitLabel')}</label>
          <div className={styles.currencyInput}>
            <span className={styles.currencyPrefix} aria-hidden>{currency === 'CAD' ? 'CA$' : 'US$'}</span>
            <input id="tariff-idle-maximum" aria-label={`${idleText(locale, 'limitLabel')} (${currency})`} className={styles.input} type="number" min={0} step={0.01} value={idleOn ? idleLimit : ''}
              onChange={e => setIdleLimit(e.target.value)} placeholder={idleText(locale, 'noLimit')} disabled={!idleOn} />
          </div>
          <p className={styles.hint}>{renderInline(idleText(locale, 'limitHint'))}</p>
        </div>
        <div className={styles.field}>
          <label className={styles.label}>Idle grace (minutes)</label>
          <input
            className={styles.input}
            type="number"
            step={1}
            min={IDLE_GRACE_MIN_MINUTES}
            max={IDLE_GRACE_MAX_MINUTES}
            value={idleGrace}
            onChange={(e) => setIdleGrace(e.target.value)}
            disabled={!hasIdleFee || rateType === 'per_hour'}
            required={hasIdleFee && rateType !== 'per_hour'}
          />
          <p className={styles.hint} style={{ fontVariantNumeric: 'tabular-nums' }}>
            {renderInline(idleText(locale, 'graceHint'))}
          </p>
        </div>
        {/* The "Session start fee (¢)" input is gone (Tier 2). A legacy
            value is shown read-only so an operator can see why an old
            session carried a fee this form cannot set. */}
        {legacyFlatFeeCents > 0 ? (
          <div className={styles.field}>
            <label className={styles.label}>Legacy session fee</label>
            <p className={styles.hint} style={{ fontVariantNumeric: 'tabular-nums' }}>
              {legacyFlatFeeCents}¢ — no longer billed on new sessions;
              EVI&rsquo;s connection fee is set per charger. Saving this
              tariff removes it.
            </p>
          </div>
        ) : (
          <div className={styles.field} />
        )}
      </div>

      {type === 'time_of_use' && (
        <div>
          <label className={styles.label} style={{ marginBottom: 8, display: 'block' }}>TOU Schedule</label>
          <TimeWindowRows<TouEntry>
            windows={touEntries}
            onChange={setTouEntries}
            // Tariff schema uses singular `day_of_week`; accessor
            // adapts to the shared component's days API without
            // touching the persisted shape.
            getDays={(e) => e.day_of_week}
            setDays={(e, days) => ({ ...e, day_of_week: days })}
            getStartHour={(e) => e.start_hour}
            setStartHour={(e, h) => ({ ...e, start_hour: h })}
            getEndHour={(e) => e.end_hour}
            setEndHour={(e, h) => ({ ...e, end_hour: h })}
            createEmpty={() => ({ day_of_week: [], start_hour: 0, end_hour: 24, energy_rate: 30 })}
            extraColumn={{
              label: 'Rate (¢/kWh)',
              render: (entry, onRowChange, disabled) => (
                <input
                  className={styles.input}
                  type="number"
                  step="0.01"
                  min={0}
                  value={entry.energy_rate}
                  onChange={(ev) => onRowChange({
                    ...entry,
                    energy_rate: parseFloat(ev.target.value) || 0,
                  })}
                  disabled={disabled}
                />
              ),
            }}
            emptyStateText="No TOU entries — add one to define a window."
            addButtonText="Add TOU entry"
            allowEmpty={false}
          />
        </div>
      )}

      {type === 'tiered' && (
        <div>
          <label className={styles.label} style={{ marginBottom: 8, display: 'block' }}>Tiered Schedule</label>
          <div className={styles.connectorList}>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) 44px', gap: 8, fontSize: 11, fontWeight: 600, color: 'var(--color-tx-m)', textTransform: 'uppercase', padding: '0 4px' }}>
              {/* Label reads "Up to" without a fixed unit — Commit D
                  made tier_max unit-neutral; the actual unit depends
                  on the tariff's rate_type. Empty value = "and above". */}
              <span>Up to — empty = ∞</span>
              <span>Rate</span>
              <span></span>
            </div>
            {tierEntries.map((t, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) 44px', gap: 8 }}>
                <input
                  className={styles.input}
                  type="text"
                  value={t.tier_max ?? ''}
                  placeholder="∞ (last tier)"
                  onChange={(ev) => setTierEntries(p => p.map((x, j) => j === i ? {
                    ...x,
                    tier_max: ev.target.value === '' ? null : parseFloat(ev.target.value) || null,
                  } : x))}
                />
                <input
                  className={styles.input}
                  type="number"
                  step="0.01"
                  min={0}
                  value={t.energy_rate}
                  onChange={(ev) => setTierEntries(p => p.map((x, j) => j === i ? { ...x, energy_rate: parseFloat(ev.target.value) || 0 } : x))}
                />
                <button type="button" className={styles.removeBtn} onClick={() => setTierEntries(p => p.filter((_, j) => j !== i))} disabled={tierEntries.length <= 1}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
          <button type="button" className={styles.addBtn} style={{ marginTop: 10 }}
            onClick={() => setTierEntries(p => [...p, { tier_max: null, energy_rate: 20 }])}>
            <Plus size={14} /> Add tier
          </button>
        </div>
      )}

      <Switch checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} label="Set as default tariff for this tenant" />

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
        <button type="button" className={styles.btnSecondary} onClick={onCancel} disabled={submitting}>
          Cancel
        </button>
        <button type="submit" className={styles.btnPrimary} disabled={submitting || !name}>
          {submitting ? 'Saving...' : (existing ? 'Update Tariff' : 'Create Tariff')}
        </button>
      </div>
    </form>
  );
}
